import { forwardRef, useMemo, useState } from 'react'
import {Coins, PiggyBank} from 'lucide-react'
import SolverCard from '@/components/SolverCard'
import CopyButton from '@/components/CopyButton'
import EmailSolutionButton from '@/components/EmailSolutionButton'

interface Props { highlighted: boolean }

const InterestCalculator = forwardRef<HTMLElement, Props>(({ highlighted }, ref) => {
  const [P, setP] = useState(1000)
  const [r, setR] = useState(5)
  const [t, setT] = useState(3)

  const interest = P * (r / 100) * t
  const total = P + interest

  const principalCoins = Math.min(40, Math.max(1, Math.round(P / 100)))
  const interestCoins = Math.min(60, Math.max(0, Math.round(interest / 50)))

  const md = useMemo(() => `# Simple Interest\n\n- Principal P = ${P}\n- Rate r = ${r}%\n- Time t = ${t} years\n- Formula: I = P · r · t\n- Computation: I = ${P} · ${(r / 100).toFixed(4)} · ${t}\n- Interest: **I = ${interest.toFixed(2)}**\n- Final amount: P + I = ${total.toFixed(2)}`, [P, r, t, interest, total])

  const Slider = ({ label, val, set, min, max, step, prefix, suffix }: { label: string, val: number, set: (n: number) => void, min: number, max: number, step?: number, prefix?: string, suffix?: string }) => (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
        <span className="font-mono-math text-emerald-300">{prefix}{val.toFixed(2)}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} step={step ?? 1} value={val} onChange={e => set(Number.parseFloat(e.target.value))} className="range-pro" />
    </div>
  )

  // Coin SVG renderer
  const renderStack = (count: number, baseX: number, color: string) => {
    const items = []
    const rows = 5
    for (let i = 0; i < count; i += 1) {
      const col = Math.floor(i / rows)
      const row = i % rows
      const x = baseX + col * 28
      const y = 220 - row * 12
      items.push(
        <g key={`${baseX}-${i}`} style={{ animation: `coinIn 0.4s ease ${i * 0.02}s both` }}>
          <ellipse cx={x} cy={y + 4} rx={18} ry={4} fill="rgba(0,0,0,0.4)" />
          <ellipse cx={x} cy={y} rx={20} ry={6} fill={color} />
          <ellipse cx={x} cy={y - 2} rx={20} ry={6} fill="none" stroke={color} strokeWidth={1.5} opacity={0.4} />
          <text x={x} y={y + 2} textAnchor="middle" fontSize="8" fontFamily="JetBrains Mono" fill="rgba(0,0,0,0.55)">$</text>
        </g>,
      )
    }
    return items
  }

  return (
    <SolverCard
      ref={ref}
      id="interest"
      eyebrow="Financial · 22,200/mo"
      title="Simple Interest Equation & Calculator (Stack Your Coins)"
      volume="Vol 22.2K"
      highlighted={highlighted}
    >
      <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-6">
        <strong className="text-slate-200">Simple interest</strong> is calculated only on the original principal — no compounding.
        The formula is <span className="font-mono-math text-emerald-300">I = P · r · t</span> where{' '}
        <span className="font-mono-math text-emerald-300">P</span> is the principal,{' '}
        <span className="font-mono-math text-emerald-300">r</span> is the annual rate (decimal), and{' '}
        <span className="font-mono-math text-emerald-300">t</span> is time in years. Watch coins stack as the inputs grow.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Slider label="Principal P" val={P} set={setP} min={100} max={20000} step={50} prefix="$" />
          <Slider label="Annual Rate r" val={r} set={setR} min={0.5} max={25} step={0.1} suffix="%" />
          <Slider label="Time t" val={t} set={setT} min={0.5} max={20} step={0.5} suffix=" yr" />

          <div className="space-y-2 pt-2">
            <div className="text-xs uppercase tracking-wider text-slate-400">Step-by-Step Solver</div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">1.</span> I = P · r · t
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">2.</span> I = {P} · {(r / 100).toFixed(4)} · {t}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">3.</span> I = {(P * (r / 100)).toFixed(2)} · {t}
            </div>
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] px-3.5 py-2.5 font-mono-math text-sm text-emerald-200">
              <span className="text-emerald-400/80">=</span> I = ${interest.toFixed(2)} · Total = ${total.toFixed(2)}
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-2">
              <CopyButton markdown={md} />
              <EmailSolutionButton title="Simple Interest Equation Calculator" markdown={md} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--line)] bg-[#0a0e1a] p-4 blueprint-grid relative">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            <span className="inline-flex items-center gap-1.5"><PiggyBank className="w-3 h-3" /> Coin Stack Simulator</span>
            <span className="inline-flex items-center gap-1.5"><Coins className="w-3 h-3" /> {principalCoins + interestCoins} coins</span>
          </div>
          <svg viewBox="0 0 380 260" className="w-full h-auto">
            {/* Floor */}
            <line x1={10} y1={232} x2={370} y2={232} stroke="#1e293b" strokeWidth={1} />

            {/* Principal stack */}
            <text x={70} y={26} textAnchor="middle" fontSize="11" fontFamily="JetBrains Mono" fill="#94a3b8">Principal</text>
            <text x={70} y={42} textAnchor="middle" fontSize="13" fontFamily="JetBrains Mono" fill="#6ee7ff">${P}</text>
            {renderStack(principalCoins, 50, '#6ee7ff')}

            {/* Interest stack (piggy bank) */}
            <text x={280} y={26} textAnchor="middle" fontSize="11" fontFamily="JetBrains Mono" fill="#94a3b8">Interest</text>
            <text x={280} y={42} textAnchor="middle" fontSize="13" fontFamily="JetBrains Mono" fill="#fbbf24">${interest.toFixed(2)}</text>
            {renderStack(interestCoins, 240, '#fbbf24')}
          </svg>
          <style>{`
            @keyframes coinIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      </div>
    </SolverCard>
  )
})

InterestCalculator.displayName = 'InterestCalculator'
export default InterestCalculator
